/**
 * Придумать вопросы покупателя.
 *
 * `POST /questions/generate` **ничего не сохраняет**: он синхронный, идёт до
 * двух минут и возвращает кандидатов. Движок просят выдать заведомо больше, чем
 * нужно, и после фильтра оставляют лучшие — отсеиваются вопросы, на которые
 * ответят советом, а не списком компаний (спека §6, шаг 3).
 *
 * Отсев показывается числами, а не прячется: «выдано 35, отсеяно 18 пустых и 5
 * с именем бренда» объясняет, почему кандидатов пришло меньше, чем просили, и
 * заодно показывает, что фильтр работает. Вопрос с именем бренда в постоянное
 * ядро не попадает вовсе — иначе измерение теряет смысл.
 *
 * Пока ждём, на экране шаги конвейера, а не неопределённый индикатор: у этой
 * фазы счётчика нет (app-kit.md §5).
 */

import { useState } from 'react';

import { api, idempotencyKey } from '../../api/client';
import { ErrorNotice, Pipeline } from '../../ui/state';
import { scanSteps } from '../../ui/screen-states';
import { Checkbox, SelectField, TextField } from '../../ui/fields';
import { FUNNEL_ORDER, funnelStage, funnelStageNote } from '../../ui/labels';
import { Candidates } from './Candidates';
import { countOf, int } from '@shared/src/format';
import { GENERATION_DURATION } from '@shared/src/product';
import type { components } from '../../api/schema';

type FunnelStage = components['schemas']['FunnelStage'];
type Generated = components['schemas']['QuestionCandidates'];
type BulkResult = components['schemas']['QuestionBulkResult'];

type Mode = 'fill' | 'top_up' | 'replace';

const MODE_LABEL: Record<Mode, string> = {
  fill: 'Собрать ядро с нуля',
  top_up: 'Добрать до лимита тарифа',
  replace: 'Предложить замену слабым вопросам',
};

/**
 * Объяснить выдачу целиком: сколько осталось и куда делись остальные.
 *
 * АРИФМЕТИКА ОБЯЗАНА СХОДИТЬСЯ. Экран печатал «Пришло 33, осталось 20:
 * отсеяно 0 без покупательского намерения и 0 с именем бренда» — и тринадцать
 * вопросов исчезали без имени. Причин было пять, а считались две: сервис резал
 * ещё повторами внутри выдачи, уже заведёнными вопросами, чужой ступенью
 * воронки и обрезкой по заказу, и ни одна из них не имела счётчика.
 *
 * Причины с нулём не печатаются: «отсеяно 0 повторов» — это шум, который прячет
 * настоящую причину. Обрезка названа отдельно от отсева и другим словом: эти
 * вопросы прошли все фильтры и вернутся следующей генерацией, а отсеянные нет.
 */
function generationNote(result: Generated, kept: number): string {
  const reasons: string[] = [];
  const add = (count: number | undefined, text: string) => {
    if (count) reasons.push(`${int(count)} ${text}`);
  };
  add(result.rejected_empty, 'без покупательского намерения');
  add(result.rejected_brand_name, 'с именем бренда в тексте');
  add(result.rejected_repeated, 'с повтором внутри выдачи');
  add(result.rejected_existing, 'с дублем среди заведённых');
  add(result.rejected_funnel, 'с другой ступени воронки');

  const head = `От нейросети пришло ${countOf(result.generated ?? 0, 'кандидат', 'кандидата', 'кандидатов')}, осталось ${int(kept)}`;
  const dropped = reasons.length ? `: отсеяно ${reasons.join(', ')}` : '';
  const trimmed = result.trimmed
    ? `${reasons.length ? '. Ещё' : ': '} ${int(result.trimmed)} не поместились в заказанное количество — они вернутся следующей генерацией`
    : '';
  return `${head}${dropped}${trimmed}.`;
}

export function Generate({
  brandId,
  free,
  onDone,
  onCancel,
}: {
  brandId: string;
  free: number | null;
  onDone: (result: BulkResult) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<Mode>(free !== null && free > 0 ? 'top_up' : 'fill');
  const [count, setCount] = useState(String(Math.min(20, free ?? 20)));
  const [stages, setStages] = useState<Set<FunnelStage>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<Generated | null>(null);

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const generated = await api.post<Generated>(
        `/brands/${brandId}/questions/generate`,
        {
          mode,
          count: Number(count) || 20,
          ...(stages.size > 0 ? { funnel_stages: [...stages] } : {}),
        },
        { idempotencyKey: idempotencyKey() },
      );
      setResult(generated);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  if (busy) {
    return (
      <div className="panel">
        <h2 className="panel-h">Придумываем вопросы</h2>
        <p className="stamp">
          Собираем то, что реально печатает ваш покупатель, и отсеиваем вопросы, на
          которые нейросеть ответит советом, а не списком компаний. Занимает{' '}
          {GENERATION_DURATION}.
        </p>
        <Pipeline steps={scanSteps()} current={1} />
      </div>
    );
  }

  if (result) {
    const kept = result.candidates.length;
    return (
      <div className="panel">
        <h2 className="panel-h">Что получилось</h2>
        <Candidates
          brandId={brandId}
          candidates={result.candidates}
          free={free}
          reason={MODE_LABEL[mode]}
          note={result.generated ? generationNote(result, kept) : undefined}
          onDone={onDone}
          onCancel={onCancel}
        />
      </div>
    );
  }

  return (
    <div className="panel">
      <h2 className="panel-h">Придумать вопросы покупателя</h2>
      <p className="stamp">
        Имени бренда в постоянном ядре быть не должно вообще: если вы сами вписали
        название в вопрос, упоминание в ответе ничего не доказывает. Такие кандидаты
        отсеиваются.
      </p>

      {error ? <ErrorNotice error={error} /> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void generate();
        }}
      >
        <SelectField<Mode>
          label="Что делаем"
          value={mode}
          onChange={setMode}
          options={(['fill', 'top_up', 'replace'] as Mode[]).map((value) => ({
            value,
            label: MODE_LABEL[value],
          }))}
          hint="Ядро меняется только явным подтверждением — генерация ничего не сохраняет"
        />

        <TextField
          label="Сколько кандидатов показать"
          value={count}
          onChange={(value) => setCount(value.replace(/\D/g, ''))}
          type="number"
          inputMode="numeric"
          hint={
            free === null
              ? 'Отобрать можно меньше, чем показано'
              : `В тариф помещается ещё ${countOf(free, 'вопрос', 'вопроса', 'вопросов')}`
          }
        />

        <fieldset className="stages">
          <legend>Стадии воронки</legend>
          <p className="hint">
            Пусто — возьмём все. Вопрос стадии «решение» ближе к деньгам, «узнавание» — к
            охвату
          </p>
          {FUNNEL_ORDER.map((stage) => (
            <Checkbox
              key={stage}
              label={funnelStage(stage)}
              note={funnelStageNote(stage)}
              checked={stages.has(stage)}
              onChange={(next) => {
                const set = new Set(stages);
                if (next) set.add(stage);
                else set.delete(stage);
                setStages(set);
              }}
            />
          ))}
        </fieldset>

        <div className="row-actions">
          <button className="btn btn-primary" type="submit">
            Придумать вопросы
          </button>
          <button className="btn btn-ghost" type="button" onClick={onCancel}>
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
}
